'use client'
import React, { useEffect, useState } from "react"
import Image from "next/image"
import { RxCrossCircled } from "react-icons/rx";
import TemplateCard, { templateType } from './templateCard'
import useGetPhotoRealisticViewStatus from "@/app/lib/hooks/useGetPhotoRealisticViewStatus";
import { useAppDispatch } from "@/app/lib/hooks/useAppDispatch";
import { designActions } from "@/app/lib/features/design/designSlice";
import TagEditor from "./tagEditor";
import { useLifeStyleImages, useUpdateLifeStyleImage } from "@/app/lib/hooks/useLifeStyleImages";
import { useChat } from "@/app/lib/context/ChatContext";
import Masonry from 'react-masonry-css';
const breakpoints = {
    default: 3,
    1280: 3,
    960: 2,
    640: 1,
};
const skeletons = Array.from({ length: 8 }, () => 250 + Math.floor(Math.random() * 150));
export const SkeletonCard = ({ height }: { height: number }) => (
    <div
        className="w-full bg-gray-200 animate-pulse mb-4 rounded-xl"
        style={{ height }}
    />
);
export const snapShotViewConstants = {
    allSnapShots: 'allSnapShots',
    particularSnapShot: 'particularSnapShot',
    editTagsMode: 'editTagsMode',
    zoomView: 'zoomView'
}
interface SnapShotViewProps {
    templates: any;
}
const SnapShotView: React.FC<SnapShotViewProps> = (props) => {
    const { templates } = props
    const dispatch = useAppDispatch()
    const { handleCurrentTemplateDetails, handleCurrentLifeStyleImages, fetchLifeStyleImages, handleFetchLifeStyleImages, handleSetOpenChat, handleSelectedLifeStyleImageId, handleSetIsEditMode } = useChat()
    //state
    const [currentView, setCurrentView] = useState(snapShotViewConstants.allSnapShots)
    const [activeParticularTemplateId, setActiveParticularTemplateId] = useState<string | null>(null);
    const [shouldStartPolling, setShouldStartPolling] = useState(false);
    const [selectedDesignLifeStyleImageIdForEdit, setSelectedDesignLifeStyleImageIdForEdit] = useState<string | null>(null);
    //query
    const { data: photoRealisticViewStatusData, isFetching: isFetchingPhotoRealisticViewStatus, isError: isErrorPhotoRealisticViewStatus, error: errorPhotoRealisticViewStatus } = useGetPhotoRealisticViewStatus(activeParticularTemplateId, shouldStartPolling)
    const { data: lifeStyleImagesData, isLoading: isLoadingLifeStyleImages, isError: isErrorLifeStyleImages, error: errorLifeStyleImages, refetch: refetchLifeStyleImages, isFetching: isFetchingLifeStyleImages } = useLifeStyleImages(activeParticularTemplateId as any);
    const updateLifeStyleImageMutation = useUpdateLifeStyleImage()
    useEffect(() => {
        //to fetch lifestyle images when generation started in ai chat and also in particular snapshot view
        if (activeParticularTemplateId) {
            refetchLifeStyleImages();
        }
    }, [activeParticularTemplateId]);
    useEffect(() => {
        if (fetchLifeStyleImages) {
            refetchLifeStyleImages();
        }
    }, [fetchLifeStyleImages])
    useEffect(() => {
        //after fetching lifestyle images, setting the state to default inorder to trigger it again
        if (!isFetchingLifeStyleImages) {
            handleFetchLifeStyleImages(false)
        }
    }, [isFetchingLifeStyleImages, handleFetchLifeStyleImages])
    //effects
    useEffect(() => {
        // to start polling for photo realistic view status
        if (activeParticularTemplateId) {
            const currentSnapShot = templates.find((eachTemplate: any) => eachTemplate?.templateId === activeParticularTemplateId)
            if (currentSnapShot && currentSnapShot?.photoRealisticViewStatus?.toLocaleLowerCase() === 'initiated' || currentSnapShot?.photoRealisticViewStatus?.toLocaleLowerCase() === 'processing') {
                setShouldStartPolling(true)
            } else if (currentSnapShot?.photoRealisticViewStatus?.toLocaleLowerCase() === 'completed') {
                setShouldStartPolling(false)
            }
        }
    }, [activeParticularTemplateId, templates])
    useEffect(() => {
        //to stop polling for photo realistic view status 
        if (photoRealisticViewStatusData?.photoRealisticViewStatus.toLocaleLowerCase() === 'completed') {
            setShouldStartPolling(false)
            dispatch(designActions.updatePhotoRealisticImageUrl({ activeParticularTemplateId, photoRealisticImageUrl: photoRealisticViewStatusData?.photoRealisticImageUrl }))
        }
    }, [photoRealisticViewStatusData?.photoRealisticImageUrl, activeParticularTemplateId])
    useEffect(() => {
        //to handle current template details when activeParticularTemplateId changes 
        if (activeParticularTemplateId) {
            const currentTemplate = templates.find((eachTemplate: any) => eachTemplate?.templateId === activeParticularTemplateId)
            if (currentTemplate) {
                handleCurrentTemplateDetails(currentTemplate);
            }
        }
    }, [activeParticularTemplateId, handleCurrentTemplateDetails, templates])
    useEffect(() => {
        // to handle current life style images when lifeStyleImagesData changes
        if (!isLoadingLifeStyleImages) {
            handleCurrentLifeStyleImages(lifeStyleImagesData);
        }
    }, [lifeStyleImagesData, isLoadingLifeStyleImages, handleCurrentLifeStyleImages])

    //handlers
    function handleCurrentView(currentView: string) {
        setCurrentView(currentView)
    }
    function handleOpenParticularSnapShotView(id: string) {
        setActiveParticularTemplateId(id)
        if (id) {
            setCurrentView(snapShotViewConstants.particularSnapShot)
        }
    }
    function handleCloseParticularSnapShotView() {
        setActiveParticularTemplateId(null)
        setCurrentView(snapShotViewConstants.allSnapShots)
    }

    //ui
    const renderAllSnapShotView = () => {
        return (<>
            {(templates?.length > 0) && <div>
                <Masonry
                    breakpointCols={breakpoints}
                    className="flex w-full max-w-[1200px] gap-6 p-4"
                    columnClassName="bg-clip-padding"
                >
                    {templates?.map((eachTemplate: any, index: number) => {
                        return (<div key={eachTemplate?.templateId || index} className="mb-6 break-inside-avoid">
                            {eachTemplate?.snapShotImageUrl &&
                                <div className="rounded-xl overflow-hidden shadow-sm hover:shadow-md transition cursor-pointer border border-gray-200">

                                    <TemplateCard
                                        templateId={eachTemplate?.templateId}
                                        tags={[]}
                                        description={[]}
                                        templateImage={eachTemplate?.snapShotImageUrl}
                                        showGenerateButton={false}
                                        showTagIcon={false}
                                        handleOpenParticularSnapShotView={handleOpenParticularSnapShotView}
                                        handleCurrentView={handleCurrentView}
                                        templateType={templateType?.snapShotImage}
                                        showDeleteButton={true}
                                    />
                                </div>}</div>);
                    })}
                </Masonry>

            </div>}</>
        );
    }
    const renderParticularSnapShot = () => {
        const currentIndex = templates.findIndex((eachTemplate: any) => eachTemplate?.templateId === activeParticularTemplateId);
        const currentSnapShot = templates[currentIndex];

        const handlePrev = () => {
            if (currentIndex > 0) {
                setActiveParticularTemplateId(templates[currentIndex - 1].templateId);
            }
        };

        const handleNext = () => {
            if (currentIndex < templates.length - 1) {
                setActiveParticularTemplateId(templates[currentIndex + 1].templateId);
            }
        };

        return (
            <div className="max-w-[1200px] w-full">
                <div className="flex justify-between items-center text-sm mb-4">
                    <div className="flex space-x-10 text-teal">
                        <button
                            onClick={handlePrev}
                            className={`text-[#0D908E] hover:underline ${currentIndex === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={currentIndex === 0}
                        >
                            Previous Snapshot
                        </button>
                        <button
                            onClick={handleNext}
                            className={`text-[#0D908E] hover:underline ${currentIndex === templates.length - 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={currentIndex === templates.length - 1}
                        >
                            Next Snapshot
                        </button>
                    </div>
                    <div>
                        <button onClick={handleCloseParticularSnapShotView} className="cursor-pointer">
                            <RxCrossCircled size={24} />
                        </button>
                    </div>
                </div>

                <div className="mb-4 w-full md:h-[400px] lg:h-[500px] border border-gray-300 rounded-lg p-[10px]">
                    <TemplateCard
                        templateId={currentSnapShot?.templateId}
                        tags={[]}
                        description={[]}
                        templateImage={currentSnapShot?.snapShotImageUrl}
                        showGenerateButton={(photoRealisticViewStatusData?.photoRealisticViewStatus?.toLocaleLowerCase() === 'completed' || currentSnapShot?.photoRealisticViewStatus?.toLocaleLowerCase() === 'completed' ? true : false) && (!isLoadingLifeStyleImages)}
                        showTagIcon={false}
                        handleOpenParticularSnapShotView={() => { }}
                        handleCurrentView={handleCurrentView}
                        templateType="template"
                        showDeleteButton={false}
                    />
                </div>

                <div className="flex flex-wrap gap-10">
                    <Masonry
                        breakpointCols={breakpoints}
                        className="flex w-full max-w-[1200px] gap-6 p-4"
                        columnClassName="bg-clip-padding"
                    >
                    {/* Photo Realistic Generating or Failed State */}
                    {(photoRealisticViewStatusData?.photoRealisticViewStatus?.toLocaleLowerCase() === 'initiated' ||
                        photoRealisticViewStatusData?.photoRealisticViewStatus?.toLocaleLowerCase() === 'processing' ||
                        shouldStartPolling) && (
                            <div className="relative rounded-xl overflow-hidden shadow-lg max-w-[400px] w-full md:h-[280px] lg:h-[300px] mb-6 break-inside-avoid">
                                <div
                                    className="absolute inset-0 bg-cover bg-center filter blur-sm scale-105"
                                    style={{ backgroundImage: `url(${currentSnapShot?.snapShotImageUrl})` }}
                                />
                                <div className="absolute inset-0 bg-opacity-30 flex flex-col justify-center items-center text-white">
                                    <div className="mb-4 text-lg">
                                        {photoRealisticViewStatusData?.photoRealisticViewStatus?.toLocaleLowerCase() === 'failed'
                                            ? 'Failed'
                                            : 'Generating...'}
                                    </div>
                                    {photoRealisticViewStatusData?.photoRealisticViewStatus?.toLocaleLowerCase() !== 'failed' && (
                                        <div className="w-12 h-12 mb-4 relative">
                                            <svg className="animate-spin h-full w-full text-orange-500" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                                <div className="absolute top-2 right-2 text-orange-500 text-xl">✦</div>
                            </div>
                        )}

                        {(photoRealisticViewStatusData?.photoRealisticViewStatus?.toLocaleLowerCase() === 'completed' ||
                            currentSnapShot?.photoRealisticViewStatus?.toLocaleLowerCase() === 'completed') && (
                                <>
                                    <div className="mb-6 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition cursor-pointer border border-gray-200">
                                        <TemplateCard
                                            templateId={currentSnapShot?.templateId}
                                            tags={[]}
                                            description={[]}
                                            templateImage={photoRealisticViewStatusData?.photoRealisticImageUrl || currentSnapShot?.photoRealisticImageUrl}
                                            showGenerateButton={false}
                                            showTagIcon={false}
                                            handleOpenParticularSnapShotView={() => { }}
                                            handleCurrentView={handleCurrentView}
                                            templateType={templateType?.photoRealisticImage}
                                            showDeleteButton={false}
                                        />
                                    </div>
                                </>
                            )}

                        {/* 1. Show skeletons during initial loading */}
                        {isLoadingLifeStyleImages &&
                            skeletons.map((item: any, index: number) => (
                                <div key={`skeleton-init-${index}`} className="mb-6 break-inside-avoid">
                                    <SkeletonCard height={item} />
                                </div>
                            ))}

                        {/* 2. Show error message if load fails */}
                        {isErrorLifeStyleImages && !isLoadingLifeStyleImages && (
                            <div className="col-span-full text-center text-red-500">
                                something went wrong while fetching lifestyle images
                            </div>
                        )}

                        {/* 3. Show lifestyle images when loaded */}
                        {!isLoadingLifeStyleImages &&
                            !isErrorLifeStyleImages &&
                            lifeStyleImagesData?.slice()
                                .reverse()
                                .map((eachImage: any, index: number) => (
                                    <div key={eachImage?.designLifeStyleImageId} className="mb-6 break-inside-avoid">
                                        {eachImage?.imageUrl && (
                                            <div className="rounded-xl overflow-hidden shadow-sm hover:shadow-md transition cursor-pointer border border-gray-200">
                                                <TemplateCard
                                                    templateId={eachImage?.designLifeStyleImageId}
                                                    tags={eachImage?.aiGeneratedData?.tags?.split(',') || []}
                                                    description={eachImage?.aiGeneratedData?.designDescription || ''}
                                                    templateImage={eachImage?.imageUrl}
                                                    showGenerateButton={false}
                                                    showTagIcon={eachImage?.aiGeneratedData}
                                                    handleOpenParticularSnapShotView={() => { }}
                                                    handleCurrentView={handleCurrentView}
                                                    templateType={templateType?.lifeStyleImage}
                                                    showDeleteButton={false}
                                                    showEditButton={true}
                                                    onClickEditButton={(id) => {
                                                        handleSetOpenChat({ isOpen: true, from: templateType?.supplierLifeStyleImage });
                                                        handleSelectedLifeStyleImageId(id);
                                                        handleSetIsEditMode(true);
                                                    }}
                                                    setSelectedDesignLifeStyleImageIdForEdit={setSelectedDesignLifeStyleImageIdForEdit}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}

                        {/* 4. Append skeletons during refetch (keep in same Masonry) */}
                        {!isLoadingLifeStyleImages &&
                            isFetchingLifeStyleImages &&
                            skeletons.map((item: any, index: number) => (
                                <div key={`skeleton-fetch-${index}`} className="mb-6 break-inside-avoid">
                                    <SkeletonCard height={item} />
                                </div>
                            ))}
                    </Masonry>
                </div>

            </div>
        );
    };
    const renderEditTagsMode = () => {
        return <>
            <TagEditor 
            selectedDesignLifeStyleImageIdForEdit={selectedDesignLifeStyleImageIdForEdit} 
            lifeStyleImagesData={lifeStyleImagesData} 
            activeParticularTemplateId={activeParticularTemplateId}
            handleOpenParticularSnapShotView={handleOpenParticularSnapShotView} 
            onSave={(updatedData) => {
                updateLifeStyleImageMutation.mutate({
                    designLifeStyleImageId: selectedDesignLifeStyleImageIdForEdit,
                    aiGeneratedData: updatedData
                });
            }} />
        </>
    }
    function getCurrentView(currentView: string) {
        switch (currentView) {
            case snapShotViewConstants.allSnapShots:
                return renderAllSnapShotView()
            case snapShotViewConstants.particularSnapShot:
                return renderParticularSnapShot()
            case snapShotViewConstants.editTagsMode:
                return renderEditTagsMode()
            default:
                return null; //need to replace
        }
    }
    //root return
    return <>
        {getCurrentView(currentView)}
    </>
}
export default SnapShotView